import qs from 'qs'

import type { ApiErrorRequest, ApiRequest } from '../../common/api/@types'
import {
  ApiFetchError,
  ApiResponseParseError,
  BadRequestApiError,
  InternalServerApiError,
  NotFoundApiError,
  UnauthorizedApiError,
  UnknownApiError,
} from '../../common/api/exceptions'

const DEFAULT_HEADERS = { 'Content-Type': 'application/vnd.api+json' }

export class Api {
  url: string

  constructor(host: string, pathname: string) {
    this.url = [host, pathname].join('/')
  }
  private parseQuery(data?: ApiRequest['query']) {
    if (!data) return ''

    const queryString = qs.stringify(data, {
      encode: false,
      arrayFormat: 'brackets',
    })

    return queryString ? `?${queryString}` : ''
  }

  private parseBody<ResponseBody>(response: Response): Promise<ResponseBody> {
    return response.json()
  }

  private async parseError(request: ApiErrorRequest, response: Response) {
    const body = await this.parseBody(response).catch(() => 'Unable to parse error body')

    if (response.status === 400 || response.status === 422) {
      return new BadRequestApiError(request, response, body)
    }
    if (response.status === 401) {
      return new UnauthorizedApiError(request, response, body)
    }
    if (response.status === 404) {
      return new NotFoundApiError(request, response, body)
    }
    if (response.status === 500) {
      return new InternalServerApiError(request, response, body)
    }
    return new UnknownApiError(request, response, body)
  }

  private sanitizeUrl(path?: string | number) {
    return [this.url, path].filter(Boolean).join('/')
  }

  protected async fetch<T>(
    method: RequestInit['method'],
    { path, body, query, headers: inHeaders }: ApiRequest,
  ) {
    const url = this.sanitizeUrl(path) + this.parseQuery(query)
    const headers = inHeaders
      ? Object.entries(inHeaders).reduce<Record<string, string>>((acc, [key, value]) => {
          if (value !== undefined) {
            acc[key] = value
          }
          return acc
        }, {})
      : undefined

    const request = {
      method,
      body: body ? JSON.stringify(body) : undefined,
      headers: { ...DEFAULT_HEADERS, ...headers },
    }

    let response: Response | undefined

    try {
      response = await fetch(url, request)
    } catch (error: unknown) {
      throw new ApiFetchError(error, { ...request, url })
    }

    if (!response.ok) {
      throw await this.parseError({ ...request, url }, response)
    }

    if (response.status === 204) {
      return undefined as T
    }

    try {
      return await this.parseBody<T>(response)
    } catch (error: unknown) {
      throw new ApiResponseParseError(error, { ...request, url }, response)
    }
  }

  async get<T>(request: ApiRequest) {
    return this.fetch<T>('GET', request)
  }

  async post<T>(request: ApiRequest) {
    return this.fetch<T>('POST', request)
  }

  async put<T>(request: ApiRequest) {
    return this.fetch<T>('PUT', request)
  }

  async delete<T>(request: ApiRequest) {
    return this.fetch<T>('DELETE', request)
  }
}
